import { useState, useEffect } from "react";
import axios from "axios";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { common } from "@mui/material/colors";

function PortabilityRcTable({ fpsId, afsoCode, distCode, date, onDataLoaded }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [totals, setTotals] = useState({});
  const [commodityKeys, setCommodityKeys] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Fetch data
  useEffect(() => {
    if (!date || !fpsId) return;
    setLoading(true);

    axios.post(`${process.env.REACT_APP_URL_SIDE}/api/DetailedTrans/Rc`, {
      date, distCode, afsoCode, fpsId
    }).then(response => {
      const rawData = response.data.data || [];

      const indexedData = rawData.map((item, index) => ({
        ...item,
        originalIndex: index + 1,
      }));

      setTitle(response.data.header || "FPS Report");
      setData(indexedData);
      setMessage(response.data.message || "");
      if (indexedData.length > 0) {
        setCommodityKeys(indexedData[0].commodities.map(c => c.name));
      }
      calculateTotals(indexedData);
      if (onDataLoaded) onDataLoaded();
      setLoading(false);
    }).catch(error => {
      console.error("Error fetching FPS data:", error);
      setData([]);
      setMessage("Internal error");
      setLoading(false);
    });
  }, [date, fpsId]);

  // Filtered and searched data
  const filteredData = data.filter(row => {
    const searchLower = searchQuery.toLowerCase();

    if (!searchQuery) return true;

    return (

      row.commodities.some(c =>
        c.name.toLowerCase().includes(searchLower) ||
        c.qty.toString().includes(searchQuery)
      ) ||

      row.existing_rc_number?.toString().toLowerCase().includes(searchLower) ||

      row.scheme_short_name?.toLowerCase().includes(searchLower) ||

      (row.trans_status?.toUpperCase() === "P" || row.trans_status?.toUpperCase() === "Q"
        ? "partial online"
        : row.trans_status?.toUpperCase() === "M"
          ? "manual"
          : "authenticated"
      ).includes(searchLower) ||

      row.receipt_id?.toString().toLowerCase().includes(searchLower) ||

      row.login_time?.toLowerCase().includes(searchLower) ||

      row.amount?.toString().includes(searchQuery) ||

      row.port_check?.toLowerCase().includes(searchLower) ||

      row.auth_time?.toLowerCase().includes(searchLower) ||

      row.originalIndex?.toString().includes(searchQuery)
    );
  });
  // Pagination
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // Calculate totals dynamically
  const calculateTotals = (rows) => {
    const totalsObj = {};
    rows.forEach(row => {
      row.commodities.forEach(c => {
        totalsObj[c.name] = (totalsObj[c.name] || 0) + (parseFloat(c.qty) || 0);
      });
    });
    totalsObj.amount = rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
    setTotals(totalsObj);
  };

  // Excel export
  const exportToExcel = async (tableData, fileName = "FPS_Report.xlsx") => {
    if (!tableData.length) return;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Report");


    // Title
    worksheet.mergeCells(1, 1, 1, 6 + commodityKeys.length + 3);
    worksheet.getCell("A1").value = title;
    worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
    worksheet.getCell("A1").font = { bold: true, size: 16 };

    // Header
    const headers = [
      "Sl No", "RC No", "Scheme", "Avail Type", "Receipt No", "Date",
      ...commodityKeys, "Amount (Rs.)", "Portability", "Auth Trans Time"
    ];
    worksheet.columns = Array(headers.length).fill({ width: 15 });

    worksheet.addRow(headers).eachCell(cell => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E0E0" } };
    });

    // Data rows
    tableData.forEach(item => {
      const row = [
        item.originalIndex,
        item.existing_rc_number || "",
        item.scheme_short_name || "",
        (item.trans_status?.toUpperCase() === "P" || item.trans_status?.toUpperCase() === "Q")
          ? "Partial Online"
          : item.trans_status?.toUpperCase() === "M"
            ? "Manual"
            : "Authenticated",
        item.receipt_id || "",
        item.login_time || "",
        ...commodityKeys.map(key => {
          const comm = item.commodities.find(c => c.name === key);
          return Number(comm?.qty || 0).toFixed(2);
        }),
        Number(item.amount || 0).toFixed(2),
        item.port_check || "",
        item.auth_time || ""
      ];
      worksheet.addRow(row);
    });

    // Totals
    const totalsRow = [
      "Total", "", "", "", "", "",
      ...commodityKeys.map(key => Number(totals[key] || 0).toFixed(2)),
      Number(totals.amount || 0).toFixed(2),
      "", ""
    ];
    worksheet.addRow(totalsRow).font = { bold: true };

    // Save
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, fileName);
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Box sx={{ display: 'flex' }}><CircularProgress /></Box>
    </div>
  );

  return (
    <div>
      {data.length > 0 ? (
        <>
          <div align="right">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              style={{ padding: "5px", width: "300px", marginBottom: "10px" }}
            />
          </div>

          <table border={1} cellPadding="5" cellSpacing="0" align="center">
            <thead>
              <tr className="tableheader">
                <th colSpan={6 + commodityKeys.length + 3}>{title}</th>
              </tr>
              <tr className="tableheader">
                <th rowSpan={2}>Sl No</th>
                <th rowSpan={2}>RC No</th>
                <th rowSpan={2}>Scheme</th>
                <th rowSpan={2}>Avail Type</th>
                <th rowSpan={2}>Receipt No</th>
                <th rowSpan={2}>Date</th>
                <th colSpan={commodityKeys.length}>Quantity In Kgs</th>
                <th rowSpan={2}>Amount (Rs.)</th>
                <th rowSpan={2}>Portability</th>
                <th rowSpan={2}>Auth Trans Time</th>
              </tr>
              <tr className="tableheader">
                {commodityKeys.map(key => <th key={key}>{key}</th>)}

              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.originalIndex}</td>
                  <td>{row.existing_rc_number}</td>
                  <td>{row.scheme_short_name}</td>
                  <td>
                    {row.trans_status?.toUpperCase() === "P" || row.trans_status?.toUpperCase() === "Q"
                      ? "Partial Online"
                      : row.trans_status?.toUpperCase() === "M"
                        ? "Manual"
                        : "Authenticated"}
                  </td>
                  <td>{row.receipt_id}</td>
                  <td>{row.login_time}</td>
                  {commodityKeys.map(key => {
                    const comm = row.commodities.find(c => c.name === key);
                    return <td style={{ textAlign: 'right' }} key={key}>{Number(comm?.qty || 0).toFixed(2)}</td>;
                  })}
                  <td style={{ textAlign: 'right' }}>{Number(row.amount || 0).toFixed(2)}</td>
                  <td>{row.port_check}</td>
                  <td>{row.auth_time}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="tableheader">
                <th colSpan={6}>Total</th>
                {commodityKeys.map(key => <th style={{ textAlign: 'right' }} key={key}>{Number(totals[key] || 0).toFixed(2)}</th>)}
                <th style={{ textAlign: 'right' }}>{Number(totals.amount || 0).toFixed(2)}</th>
                <th colSpan={2}></th>
              </tr>
              <tr>
                <td colSpan={6 + commodityKeys.length + 3} align="center">
                  <button className="button" onClick={() => exportToExcel(filteredData)}>Export to Excel</button>
                </td>
              </tr>
            </tfoot>
          </table>

          <div align="right" style={{ marginTop: '10px' }}>
            <label>Rows per page: </label>
            <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
              {[10, 20, 50, 100, filteredData.length].map(n => <option key={n} value={n}>{n}</option>)}
            </select>

            <button className="button" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>First</button>
            <button className="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
            <span> Page {currentPage} of {totalPages} </span>
            <button className="button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
            <button className="button" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>Last</button>
          </div>
        </>
      ) : (
        <div align="center" className="response">{message}</div>
      )}
    </div>
  );
}

export default PortabilityRcTable;
